import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiLock, FiEye, FiEyeOff, FiArrowRight, FiShield } from 'react-icons/fi';
import { authService } from '../../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const Login = ({ setAuth }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await authService.login(credentials);
      toast.success('Access Granted. Synchronizing Interface...');
      setAuth(true);
      setTimeout(() => navigate('/admin'), 1000);
    } catch (error) {
      toast.error('Authentication Failed. Invalid Credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-6 relative overflow-hidden font-poppins font-poppins selection:bg-syana-blue-100 selection:text-syana-blue-900">
      {/* Dynamic Background Elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-syana-blue-400/10 rounded-full blur-[120px] animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[30%] bg-indigo-400/10 rounded-full blur-[100px] delay-1000 animate-pulse"></div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="text-center mb-10">
           <motion.div 
             initial={{ scale: 0.8 }}
             animate={{ scale: 1 }}
             className="w-20 h-20 bg-slate-900 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-slate-200 border border-white/10"
           >
              <FiShield className="text-white text-3xl" />
           </motion.div>
           <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">Syana Optometry</h1>
           <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mt-2">Professional Infrastructure Access</p>
        </div>

        <div className="bg-white/80 backdrop-blur-2xl p-10 rounded-2xl border border-white shadow-2xl shadow-slate-200/50">
           <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                 <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">System Identity</label>
                 <div className="relative group">
                    <FiUser className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-slate-900 transition-colors" />
                    <input 
                      type="text" 
                      placeholder="Username"
                      required
                      value={credentials.username}
                      onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                      className="w-full pl-14 pr-6 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-slate-900 focus:bg-white outline-none transition-all text-sm font-bold"
                    />
                 </div>
              </div>

              <div className="space-y-2">
                 <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">Access Key</label>
                 <div className="relative group">
                    <FiLock className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-slate-900 transition-colors" />
                    <input 
                      type={showPassword ? "text" : "password"} 
                      placeholder="Enter Password"
                      required
                      value={credentials.password}
                      onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                      className="w-full pl-14 pr-14 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-slate-900 focus:bg-white outline-none transition-all text-sm font-bold"
                    />
                    <button 
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-900 transition-colors"
                    >
                       {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                 </div>
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="w-full py-4 bg-slate-900 text-white rounded-2xl flex items-center justify-center space-x-3 shadow-xl hover:bg-slate-800 hover:translate-y-[-2px] active:translate-y-[0] transition-all disabled:opacity-50 mt-10"
              >
                 <AnimatePresence mode="wait">
                    {loading ? (
                       <motion.div 
                         key="loading" 
                         initial={{ opacity: 0 }} 
                         animate={{ opacity: 1 }} 
                         exit={{ opacity: 0 }}
                         className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"
                       />
                    ) : (
                       <motion.div 
                         key="idle" 
                         className="flex items-center space-x-3 text-[11px] font-black uppercase tracking-widest"
                       >
                          <span>Authorize Entry</span>
                          <FiArrowRight />
                       </motion.div>
                    )}
                 </AnimatePresence>
              </button>
           </form>
        </div>

        <div className="mt-8 text-center">
           <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest">Siyane Optometrists Clinical Infrastructure v1.0.5</p>
        </div>
      </motion.div>
      
      {/* Decorative side badge */}
      <div className="fixed bottom-10 left-10 hidden lg:block">
         <div className="flex items-center space-x-4 opacity-20 hover:opacity-100 transition-opacity cursor-default">
            <div className="w-10 h-[1px] bg-slate-900"></div>
            <p className="text-[10px] font-black uppercase tracking-[0.5em] text-slate-900">Secured Node</p>
         </div>
      </div>
    </div>
  );
};

export default Login;
